import { type JSX, Show, createContext, createSignal, useContext } from 'solid-js';
import { Button } from './Button';
import { Modal, createExitHold } from './Modal';

export interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

export const ConfirmProvider = (props: { children: JSX.Element }): JSX.Element => {
  const [pending, setPending] = createSignal<PendingConfirm | null>(null);

  const confirm: ConfirmFn = (opts) =>
    new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve });
    });

  const settle = (ok: boolean) => {
    pending()?.resolve(ok);
    setPending(null);
  };

  // Keep title/body rendered through the exit animation after settle() clears pending.
  const held = createExitHold(pending);

  return (
    <ConfirmContext.Provider value={confirm}>
      {props.children}
      <Modal open={pending() != null} onClose={() => settle(false)} title={held()?.title} size="sm">
        <div class="flex flex-col gap-4">
          <Show when={held()?.body}>
            <p class="text-sm text-neutral-600 dark:text-neutral-400">{held()?.body}</p>
          </Show>
          <div class="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => settle(false)}>
              Cancel
            </Button>
            <Button variant={held()?.danger ? 'danger' : 'primary'} onClick={() => settle(true)}>
              {held()?.confirmLabel ?? 'Confirm'}
            </Button>
          </div>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
};
